import type { ReactLocalization } from "@fluent/react";
import { MODEL_STR } from "byzantine/src/Role";
import type { AccountPermissionParent } from "byzantine/src/Role";

/**
 * Web label for each account-scoped permission. Fluent ids stay literal so they can be statically
 * checked. Which permissions exist, their groups and their feature gating live in byzantine.
 */
const LABELS: Record<string, (l10n: ReactLocalization) => string> = {
  [MODEL_STR.INTRA_INSTITUTION_TRANSFER]: (l10n) =>
    l10n.getString("manage-users-intra-institution-transfer"),
  [MODEL_STR.ACH_TRANSFER]: (l10n) =>
    l10n.getString("manage-users-ach-transfer"),
  [MODEL_STR.ACH_PAYMENT]: (l10n) =>
    l10n.getString("manage-users-ach-payment-without-templates"),
  [MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE]: (l10n) =>
    l10n.getString("manage-users-ach-payment-with-templates"),
  [MODEL_STR.ACH_PAYMENT_VIA_IMPORT]: (l10n) =>
    l10n.getString("org-role-account-import-ach-send"),
  [MODEL_STR.UNVERIFIED_FEDNOW_SEND]: (l10n) =>
    l10n.getString("manage-users-fednow-send"),
  [MODEL_STR.ACH_PAYMENT_REVERSE]: (l10n) =>
    l10n.getString("manage-users-reverse-payment"),
  [MODEL_STR.ACH_PAYMENT_COLLECT]: (l10n) =>
    l10n.getString("manage-users-collect-ach-payment-without-templates"),
  [MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE]: (l10n) =>
    l10n.getString("manage-users-collect-ach-payment-with-templates"),
  [MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT]: (l10n) =>
    l10n.getString("org-role-account-import-ach-collect"),
  [MODEL_STR.ACH_PAYMENT_COLLECT_REVERSE]: (l10n) =>
    l10n.getString("manage-users-reverse-payment"),
  [MODEL_STR.ACH_PASSTHROUGH_SEND]: (l10n) =>
    l10n.getString("manage-users-ach-passthrough-send"),
  [MODEL_STR.ACH_PASSTHROUGH_COLLECT]: (l10n) =>
    l10n.getString("manage-users-ach-passthrough-collect"),
  [MODEL_STR.DOMESTIC_WIRE_TRANSACTION]: (l10n) =>
    l10n.getString("org-role-account-send-without-templates"),
  [MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES]: (l10n) =>
    l10n.getString("org-role-account-send-with-templates"),
  [MODEL_STR.DOMESTIC_WIRE_IMPORT]: (l10n) =>
    l10n.getString("org-role-account-import-domestic-wire"),
  [MODEL_STR.INTERNATIONAL_WIRE]: (l10n) =>
    l10n.getString("org-role-account-send-without-templates"),
  [MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES]: (l10n) =>
    l10n.getString("org-role-account-send-with-templates"),
  [MODEL_STR.RDC]: (l10n) => l10n.getString("manage-users-rdc"),
  [MODEL_STR.BULK_PAYMENT_TEMPLATE]: (l10n) =>
    l10n.getString("manage-users-ach-instant-manage-templates"),
  [MODEL_STR.DOMESTIC_WIRE_TEMPLATES]: (l10n) =>
    l10n.getString("manage-users-domestic-wire-manage-templates"),
  [MODEL_STR.INTERNATIONAL_WIRE_TEMPLATES]: (l10n) =>
    l10n.getString("manage-users-international-wire-manage-templates"),
};

const PARENT_LABELS: Record<
  AccountPermissionParent,
  (l10n: ReactLocalization) => string
> = {
  sendAchPayments: (l10n) => l10n.getString("org-role-account-send-ach"),
  collectAchPayments: (l10n) => l10n.getString("org-role-account-collect-ach"),
  sendDomesticWires: (l10n) =>
    l10n.getString("org-role-account-send-domestic-wires"),
  sendInternationalWires: (l10n) =>
    l10n.getString("org-role-account-send-international-wires"),
  importPaymentFiles: (l10n) =>
    l10n.getString("org-role-account-import-payment-files"),
};

export const getAccountPermissionParentLabel = (
  parent: AccountPermissionParent,
  l10n: ReactLocalization
): string => PARENT_LABELS[parent](l10n);

const getAccountPermissionLabel = (
  modelStr: string,
  l10n: ReactLocalization
): string => LABELS[modelStr]?.(l10n) ?? modelStr;

export default getAccountPermissionLabel;
